import { ref, watch } from "vue";
import { getRefs } from "./utils";
import classNames from "classnames/dedupe";

interface ReferenceNavProps {
  rootEl: HTMLElement;
}

const sections = ["intro","beschreibung", "bilder", "technische-details", "kontakt"];

const ReferenceNav = (props: ReferenceNavProps) => {
  const top = ref(false);

  const handleWindowScroll = () => {
    top.value = window.scrollY > props.rootEl.offsetTop;
  };

  const refs = getRefs<{
    nav: HTMLElement;
    scroll: HTMLElement;
    intro: HTMLElement;
  }>(props.rootEl);

  if (!refs.nav) return;

  window.addEventListener("scroll", handleWindowScroll);
  handleWindowScroll();

  watch(
    top,
    (value) => {
      if (!refs.nav) return;
      refs.nav.classList.toggle("fixed", value);
    },
    { immediate: true, deep: true }
  );

  const navEls: { section: string; link: HTMLElement }[] = [];
  for (const section of sections) {
    const sectionEl = document.getElementById(section);
    if (!sectionEl) continue;

    const navEl = document.createElement("a");
    navEl.setAttribute("href", `#${section}`);
    navEl.innerText = section.replace(/-/g, " ");
    navEl.classList.add("reference--nav--item");
    navEls.push({ section, link: navEl });
  }

  refs.scroll.append(...navEls.map((el) => el.link));
  NavLinks(navEls);

  if (!refs.scroll) return;
  ScrollShadow({ rootEl: refs.scroll });
};

const NavLinks = (props: { section: string; link: HTMLElement }[]) => {
  let activeLink: HTMLElement | null = null;

  for (const { section, link } of props) {
    const sectionEl = document.getElementById(section);
    if (!sectionEl) continue;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          if (activeLink) {
            activeLink.setAttribute("aria-current", "false");
          }
          link.setAttribute("aria-current", "true");
          activeLink = link;
        }
      },
      {
        rootMargin: "-50% 0px -50% 0px",
      }
    );

    observer.observe(sectionEl);
  }
};

// Add scroll shadows to the nav when its overflowing (mobile)
const ScrollShadow = (props: { rootEl: HTMLElement }) => {
  const navScrollState = ref({
    left: false,
    right: false,
  });

  const handleScroll = (el: HTMLElement) => {
    navScrollState.value.left = el.scrollLeft > 0;
    navScrollState.value.right = Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth;
  };

  props.rootEl.addEventListener("scroll", (e) => handleScroll(e.target as HTMLElement));
  props.rootEl.addEventListener("resize", (e) => handleScroll(e.target as HTMLElement));
  window.addEventListener("resize", () => handleScroll(props.rootEl));
  handleScroll(props.rootEl);

  watch(
    navScrollState,
    (value) => {
      const classes = classNames(props.rootEl.className, {
        left: value.left,
        right: value.right,
      });

      props.rootEl.className = classes;
    },
    { immediate: true, deep: true }
  );
};

const init = () => {
  const referenceNavs = document.querySelectorAll<HTMLElement>("[h-comp='referenceNav']");

  for (const referenceNav of referenceNavs) {
    ReferenceNav({ rootEl: referenceNav });
  }
};

init();
